import React, { useCallback, useEffect, useMemo, useState } from "react";
import Container from "@material-ui/core/Container";
import { connect } from "react-redux";
import {
  useDependency,
  useEntityProgress,
  useMasterDetailWithDialog,
  useSortHandler,
} from "reactcoregk/hooks";
import AlertTable from "./alert-table";
import AlertSubheader from "./alert-subheader";
import {
  useCurrentPageParam,
  useDefaultTable,
  usePageableEntity,
  useQuery,
  useSortHandlerPath,
  useTableCells,
} from "../../helpers/hooks";
import SubmenuLayout from "../../components/Layouts/SubmenuLayout";
import {
  createAlert,
  deleteAlert,
  getAllAlerts,
  updateAlert,
} from "../../store/alert/actions";
import EditDialog from "./EditDialog";
import { Schema } from "../../store/@core/schema";
import { EntityType } from "../../store/@core/entityType";
import { createOptions } from "reactcoregk/utils";
import Grid from "@material-ui/core/Grid";
import { Pagination } from "@material-ui/lab";
import Box from "@material-ui/core/Box";
import { getAllTechniques } from "../../store/technique/actions";
import moment from "moment";
import TableSettingsDialog from "components/Common/TableSettingsDialog";
import { tableSettingKeys } from "constants/enums";
const defaultCells = [
  {
    id: "id",
    label: "Alert ID",
    isSortable: true,
    default: true,
  },
  {
    id: "shortName",
    label: "Short Name",
    default: true,
  },
  {
    id: "category",
    label: "Category",
    default: true,
  },
  {
    id: "cves",
    label: "CVEs",
    default: true,
  },
  {
    id: "attacks",
    label: "Attacks",
    default: true,
  },
  {
    id: "actors",
    label: "Actors",
    default: true,
  },
  {
    id: "cisa",
    label: "CISA",
    default: true,
  },
  {
    id: "threatLevel",
    label: "Threat Level",
    default: true,
  },
  {
    id: "lastUpdatedAt",
    label: "Last Update",
    isSortable: true,
    default: true,
  },
  {
    id: "createdByName",
    label: "Created By",
    default: true,
  },
];

function Alerts(props) {
  const {
    context,
    getAllAlerts,
    createAlert,
    getAllTechniques,
    updateAlert,
    deleteAlert,
  } = props;

  const [openSettings, setOpenSettings] = useState(false);

  // Handlers for subheader controls
  // const { query, handleChangeQuery } = useSubheaderLogic();
  const [currentPage, setCurrentPage] = useCurrentPageParam();
  const query = useQuery();
  const s = query.get("s") || "";

  // Track previous filters to detect when they change and reset page
  const prevFiltersRef = React.useRef("");
  const isFirstRenderRef = React.useRef(true);

  // Calculate rootParams with filter-aware page reset
  const rootParams = useMemo(() => {
    const urlParams = new URLSearchParams(props.location.search);
    urlParams.delete("page");
    urlParams.sort(); // Sort params for consistent comparison
    const currentFilters = urlParams.toString();

    // Determine which page to use
    let pageToUse = currentPage - 1;

    // If filters changed since last render (not first render), always use page 0
    if (
      !isFirstRenderRef.current &&
      currentFilters !== prevFiltersRef.current
    ) {
      pageToUse = 0;
      // Don't update the ref here - let useEffect handle it
    }

    const paramsString = urlParams.toString();
    return `&page=${pageToUse}${paramsString ? "&" + paramsString : ""}`;
  }, [currentPage, props.location.search]);

  // Update URL to page=0 when filters change (to sync UI)
  useEffect(() => {
    const urlParams = new URLSearchParams(props.location.search);
    const currentPageFromUrl = parseInt(urlParams.get("page")) || 0;
    urlParams.delete("page");
    urlParams.sort();
    const currentFilters = urlParams.toString();

    // If not first render
    if (!isFirstRenderRef.current) {
      // If filters changed, force page to 0
      if (currentFilters !== prevFiltersRef.current) {
        if (currentPageFromUrl !== 0) {
          setCurrentPage(0);
          // Don't update ref yet - wait for page to become 0
          return;
        }
      }
      // Only update ref when page is 0 or filters haven't changed
      prevFiltersRef.current = currentFilters;
    }

    isFirstRenderRef.current = false;
  }, [props.location.search, setCurrentPage]);

  // Handlers for new or existing entity to show in dialog
  const {
    entity,
    handleEntityClick,
    openModal,
    handleCloseModal,
    handleCreateNew,
  } = useMasterDetailWithDialog(Schema[EntityType.Alert]);

  // Create the subheader options
  const AlertMenu = (
    <AlertSubheader
      handleCreateNew={handleCreateNew}
      openSettings={() => setOpenSettings(true)}
    />
  );

  // Sort handler used by default table
  const sortHandler = useSortHandler("lastUpdatedAt", "desc");

  // Callback to trigger on useDependency hook
  const initializeDependency = useCallback(() => {
    getAllAlerts("?page=0&size=20");
  }, [getAllAlerts]);

  // Initialize dependency
  useDependency(context.Technique, getAllTechniques);

  // Refresh alerts after RDU
  useEntityProgress(context.Alert, initializeDependency);

  const { cells, storedCells, optionalCellRender } = useTableCells(
    context,
    tableSettingKeys.ALERTS,
    defaultCells,
    false
  );

  // Get state for current entity, and handler to manage paging
  const { list, handleLoadMore, totalPages, isLoading } = usePageableEntity(
    context.Alert,
    getAllAlerts,
    rootParams
  );

  useSortHandlerPath(sortHandler.sort);

  const defaultTable = useDefaultTable(list, sortHandler);

  const formatted = list.map((x) => ({
    ...x,
    createdByName: x.createdBy?.username,
    lastUpdatedAt: moment(x.lastUpdatedAt).format("lll"),
  }));

  // Create options
  const countryOptions = createOptions(
    context.Country.getAll.result,
    "name",
    "name"
  );
  const verticalOptions = createOptions(
    context.CompanyVertical.getAll.result,
    "name",
    "name"
  );

  return (
    <SubmenuLayout MenuItemsComponent={AlertMenu} title={"Alerts"}>
      <Container maxWidth={false}>
        <AlertTable
          headCells={cells}
          alerts={formatted}
          defaultTable={defaultTable}
          handleEntityClick={handleEntityClick}
          sortHandler={sortHandler}
          handleLoadMore={handleLoadMore}
          isLoading={isLoading}
          optionalCellRender={optionalCellRender}
        />
        <Box mt={2} component={Grid} container justify={"center"}>
          <Pagination
            onChange={(e, newPage) => setCurrentPage(newPage - 1)}
            page={currentPage}
            count={totalPages}
            showFirstButton
            showLastButton
            size={"small"}
          />
        </Box>
      </Container>
      <EditDialog
        context={context.Alert}
        entity={entity}
        techs={context.Technique.getAll.result}
        entityType={EntityType.Alert}
        open={openModal}
        handleClose={handleCloseModal}
        createEntity={createAlert}
        updateEntity={updateAlert}
        countryOptions={countryOptions}
        verticalOptions={verticalOptions}
        deleteEntity={deleteAlert}
      />

      <TableSettingsDialog
        open={openSettings}
        handleClose={() => setOpenSettings(false)}
        storedCells={storedCells}
        defaultCells={defaultCells}
        tableKey={tableSettingKeys.ALERTS}
      />
    </SubmenuLayout>
  );
}

const mapStateToProps = (state) => {
  return {
    context: state,
  };
};
export default connect(mapStateToProps, {
  getAllAlerts,
  createAlert,
  getAllTechniques,
  updateAlert,
  deleteAlert,
})(Alerts);
